/**
 * Growth & Trends Tab
 * V6.15 V2: Detailed growth analytics with cumulative, weekly, and MRR charts
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Skeleton } from '@mui/material';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import apiService from '../../../services/api';

interface GrowthData {
  kpis: {
    newThisPeriod: number;
    netNew: number;
    mrrGrowth: number;
    annualRate: number;
  };
  daily: { date: string; newSubs: number; churned: number }[];
  weekly: { week: string; monthlyNew: number; annualNew: number; totalNew: number }[];
  cumulative: { date: string; active: number }[];
  tierTrends: { week: string; tier: string; count: number }[];
}

const KpiCard: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '24px', fontWeight: 800, color: color || '#111827', mt: 0.5 }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#9CA3AF', mt: 0.25 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

const formatDate = (d: string) => {
  const date = new Date(d + 'T00:00:00');
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

interface GrowthTrendsTabProps {
  timeRangeDays?: string;
}

const GrowthTrendsTab: React.FC<GrowthTrendsTabProps> = ({ timeRangeDays }) => {
  const [data, setData] = useState<GrowthData | null>(null);
  const [loading, setLoading] = useState(true);
  const days = timeRangeDays ?? '90';

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      try {
        const response = await apiService.get(`/api/admin/subscriptions/intelligence/growth-trends?days=${days}`);
        if (response.data) {
          setData(response.data as GrowthData);
        }
      } catch (e) {
        console.error('Failed to load growth trends:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days]);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
          {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />)}
        </Box>
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', color: '#6B7280' }}>
          Unable to load growth trends data
        </Typography>
      </Box>
    );
  }

  // Build weekly new vs churned
  const weeklyNewVsChurned = data.weekly.map(w => {
    const weekDays = data.daily.filter(d => {
      const dDate = new Date(d.date + 'T00:00:00');
      const wDate = new Date(w.week + 'T00:00:00');
      const diff = dDate.getTime() - wDate.getTime();
      return diff >= 0 && diff < 7 * 86400000;
    });
    const churned = weekDays.reduce((s, d) => s + d.churned, 0);
    return { week: w.week, newSubs: w.totalNew, churned };
  });

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {/* KPI Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <KpiCard label="New This Period" value={data.kpis.newThisPeriod.toLocaleString()} sub={`Last ${days} days`} color="#1A5F7A" />
        <KpiCard label="Net New" value={data.kpis.netNew >= 0 ? `+${data.kpis.netNew}` : `${data.kpis.netNew}`} sub="New minus churned" color={data.kpis.netNew >= 0 ? '#10B981' : '#EF4444'} />
        <KpiCard label="Current MRR" value={`$${data.kpis.mrrGrowth.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} sub="Monthly recurring revenue" color="#D4A843" />
        <KpiCard label="Annual Rate" value={`${data.kpis.annualRate}%`} sub="Target: 70%" color={data.kpis.annualRate >= 70 ? '#10B981' : '#6B7280'} />
      </Box>

      {/* Cumulative Active Subscribers */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
          Cumulative Active Subscribers
        </Typography>
        <div style={{ width: '100%', height: 280, minHeight: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.cumulative}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fontFamily: 'Montserrat' }} />
              <YAxis tick={{ fontSize: 10, fontFamily: 'Montserrat' }} />
              <Tooltip
                contentStyle={{ fontFamily: 'Montserrat', fontSize: 12, borderRadius: 8 }}
                labelFormatter={formatDate}
              />
              <defs>
                <linearGradient id="tealGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1A5F7A" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#1A5F7A" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="active" stroke="#1A5F7A" fill="url(#tealGrad)" strokeWidth={2} name="Active Subscribers" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Paper>

      {/* New vs Churned (Weekly) */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
          New vs Churned (Weekly)
        </Typography>
        <div style={{ width: '100%', height: 220, minHeight: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weeklyNewVsChurned}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="week" tickFormatter={formatDate} tick={{ fontSize: 10, fontFamily: 'Montserrat' }} />
              <YAxis tick={{ fontSize: 10, fontFamily: 'Montserrat' }} />
              <Tooltip contentStyle={{ fontFamily: 'Montserrat', fontSize: 12, borderRadius: 8 }} labelFormatter={formatDate} />
              <Bar dataKey="newSubs" fill="#10B981" name="New" radius={[4, 4, 0, 0]} />
              <Bar dataKey="churned" fill="#EF4444" name="Churned" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Paper>

      {/* MRR Trend */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
          MRR Trend
        </Typography>
        <div style={{ width: '100%', height: 220, minHeight: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.cumulative.map(c => ({ date: c.date, mrr: parseFloat((c.active * (data.kpis.mrrGrowth / Math.max(data.kpis.newThisPeriod + data.kpis.netNew, 1))).toFixed(2)) || data.kpis.mrrGrowth }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fontFamily: 'Montserrat' }} />
              <YAxis tick={{ fontSize: 10, fontFamily: 'Montserrat' }} tickFormatter={(v: number) => `$${v}`} />
              <Tooltip contentStyle={{ fontFamily: 'Montserrat', fontSize: 12, borderRadius: 8 }} labelFormatter={formatDate} formatter={(v: number) => [`$${v.toFixed(2)}`, 'MRR']} />
              <defs>
                <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D4A843" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#D4A843" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="mrr" stroke="#D4A843" fill="url(#goldGrad)" strokeWidth={2} name="MRR" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Paper>

      {/* Annual Rate Trend */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
          Annual Subscription Rate
        </Typography>
        <div style={{ width: '100%', height: 200, minHeight: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.weekly.map(w => ({
              week: w.week,
              annualRate: w.totalNew > 0 ? parseFloat(((w.annualNew / w.totalNew) * 100).toFixed(1)) : 0
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="week" tickFormatter={formatDate} tick={{ fontSize: 10, fontFamily: 'Montserrat' }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fontFamily: 'Montserrat' }} tickFormatter={(v: number) => `${v}%`} />
              <Tooltip contentStyle={{ fontFamily: 'Montserrat', fontSize: 12, borderRadius: 8 }} labelFormatter={formatDate} formatter={(v: number) => [`${v}%`, 'Annual Rate']} />
              <ReferenceLine y={70} stroke="#D4A843" strokeDasharray="6 4" label={{ value: '70% Target', position: 'right', fontSize: 10, fill: '#D4A843', fontFamily: 'Montserrat' }} />
              <Line type="monotone" dataKey="annualRate" stroke="#1A5F7A" strokeWidth={2} dot={{ r: 3 }} name="Annual Rate" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Paper>
    </Box>
  );
};

export default GrowthTrendsTab;
